import React, { useContext, useEffect, useRef, useState } from "react";
import { Calendar } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import { ModalContext } from "context/ModalContext";
import { Box, TableContainer } from "@mui/material";
import interactionPlugin from "@fullcalendar/interaction";
import PropTypes from "prop-types";
import BookingForm from "./Form";
import { bookingList } from "Apis/booking";
import { getBookingById } from "Apis/booking";
import { LoginFormContext } from "context/LoginFormContext";
import Iconify from "components/Iconify";
import colors from "assets/theme/base/colors";
import SubmitButton from "components/SubmitButton";
import MDModal from "components/MDModal";

const Booking = () => {
  const bookingRef = useRef(null);
  const { showMessageNotification, formIsEdit, setOpen, setFormIsEdit } = useContext(ModalContext);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedRange, setSelectedRange] = useState({ start: null, end: null });
  const [editData, setEditData] = useState({});
  const [deleteId, setDeleteId] = useState(null);
  const { isLoading, setIsLoading } = useContext(LoginFormContext);
  const [bookingData, setBookingData] = useState([]);
  const { info, sideNav } = colors;

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "background.paper",
    borderRadius: "8px",
    maxWidth: "870px",
    width: "90%",
    overflowY: "auto",
    overflowX: "hidden",
    maxHeight: "90%",
    height: "auto",
    p: 4,
    pt: 0,
    "&:focus": {
      outline: "none",
    },
  };

  const bookingListData = async () => {
    let response = await bookingList();
    setIsLoading(true);
    if (response?.status === 200) {
      const formattedData = response?.data?.data?.rows?.map((item) => {
        const startDateTime = `${item?.start_date}T${item?.start_time}`;
        let endDateTime;
        if (item?.end_time === "00:00:00") {
          endDateTime = `${item?.end_date}T23:59:59`;
        } else {
          endDateTime = `${item?.end_date}T${item?.end_time}`;
        }

        return {
          id: item.id,
          title: item?.name,
          start: startDateTime,
          end: endDateTime,
          backgroundColor: item?.color,
          borderColor: item?.color,
          textColor: "#ffffff",
        };
      });
      setBookingData(formattedData);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    bookingListData();
  }, []);

  const handleSelect = (info) => {
    const startDateTime = new Date(info.startStr);
    let endDateTime = new Date(info.endStr);
    if (info?.view?.type === "dayGridMonth") {
      endDateTime.setDate(endDateTime.getDate() - 1);
    }
    if (info?.view?.type === "timeGridWeek" && info.allDay === true) {
      endDateTime.setDate(endDateTime.getDate() - 1);
    }

    setSelectedRange({ start: info.startStr, end: endDateTime.toISOString().split("T")[0] });
    setFormIsEdit(false);
    setEditData({});
    setOpen(true);
  };

  const handleEventClick = async (info) => {
    const response = await getBookingById(info.event.id);
    if (response?.status === 200) {
      setDeleteId(info.event.id);
      setFormIsEdit(true);
      setEditData(response.data?.data);
      setOpen(true);
    } else {
      showMessageNotification("Error fetching event data");
    }
  };

  const bookingColorOptions = [
    { label: "Marriage & Other Functions of Members", color: "#880808" },
    { label: "AGRT Functions", color: "#006400" },
    { label: "Non Member Functions", color: "#040348" },
  ];

  const calendarRef = useRef(null);
  const isFirstRenderRef = useRef(true);
  const lastViewDateRef = useRef(null);
  const futureViewDataRef = useRef(null);

  useEffect(() => {
    if (!isLoading && !calendarRef.current) {
      const calendarEl = bookingRef.current;
      const calendar = new Calendar(calendarEl, {
        plugins: [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
        initialView: "dayGridMonth",
        initialDate: new Date(),
        headerToolbar: {
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth,timeGridWeek,timeGridDay",
        },
        weekends: true,
        editable: true,
        selectable: true,
        select: handleSelect,
        selectAllow: (selectInfo) => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          // return selectInfo.start >= today;
          return selectInfo.start;
        },
        eventTimeFormat: {
          hour: "2-digit",
          minute: "2-digit",
          meridiem: "short",
          hour12: true,
        },
        eventClick: handleEventClick,
        events: bookingData,
        eventContent: (info) => {
          const isAllDay = info.event.allDay;
          const startTime = info.event.start
            ? new Date(info.event.start).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                meridiem: "short",
                hour12: true,
              })
            : null;
          const endTime =
            info.event.end && !isAllDay
              ? new Date(info.event.end).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  meridiem: "short",
                  hour12: true,
                })
              : null;
          return {
            html: `
            <div style="
              align-items: center;
              background-color: ${info.event.backgroundColor};
              padding: 2px 5px;
              border-radius: 4px;
              max-width: 100%;
              box-sizing: border-box;
              color: ${info.event.textColor};
            ">
              ${!isAllDay && startTime ? `<span style="white-space: normal; word-break: break-word; flex: 1;">${startTime}</span>` : ""}
              ${!isAllDay && endTime ? `<span style="margin-left: 5px; white-space: normal; word-break: break-word; flex: 1;"> - ${endTime}</span>` : ""}
              <span style="white-space: normal; word-break: break-word; flex: 1;">${info.event.title}</span>
            </div>
          `,
          };
        },
        datesSet: (arg) => {
          lastViewDateRef.current = arg.start;
          futureViewDataRef.current = new Date(arg.end.getTime() - 1);
        },
      });

      // Custom styles
      const customStyles = document.createElement("style");
      customStyles.innerHTML = `
      .fc .fc-toolbar .fc-button {
        font-size: 12px !important;
        padding: 6px 10px !important;
        font-weight: 500 !important;
        min-width: 40px !important;
        min-height: 25px !important;
        background: ${info.main} !important;
        color: ${info.color} !important;
        border: 1px solid ${info.color} !important;
      }
      .fc .fc-toolbar .fc-button:hover {
        background: ${sideNav.hoverTextColor} !important;
        color: ${sideNav.hover} !important;
      }
      .fc .fc-button:disabled { opacity: 1 !important; }
      .fc .fc-button:active {
        background: ${sideNav.hoverTextColor} !important;
        color: ${sideNav.hover} !important;
      }
      .fc .fc-toolbar.fc-header-toolbar { margin-top: 0.5rem !important; box-shadow: none !important; }
      .fc .fc-button-group .fc-button:active { box-shadow: none !important; }
      .fc .fc-button-primary.fc-button-active {
        background: ${sideNav.hoverTextColor} !important;
        color: ${sideNav.hover} !important;
        box-shadow: none !important;
      }
    `;
      document.head.appendChild(customStyles);

      calendar.render();
      calendarRef.current = calendar;

      return () => {
        if (calendarRef.current) {
          calendarRef.current.destroy();
          calendarRef.current = null;
        }
      };
    }
  }, [isLoading]);

  useEffect(() => {
    if (calendarRef.current) {
      const calendar = calendarRef.current;

      calendar.removeAllEvents();
      calendar.addEventSource(bookingData);

      const today = new Date();
      const visibleStart = lastViewDateRef.current;
      const visibleEnd = futureViewDataRef.current;

      const eventInCurrentView = bookingData.find((event) => {
        const eventDate = new Date(event.start);
        return eventDate >= visibleStart && eventDate < visibleEnd;
      });

      if (eventInCurrentView) {
        calendar.gotoDate(new Date(eventInCurrentView.start));
      } else if (isFirstRenderRef.current) {
        calendar.gotoDate(today);
      }

      isFirstRenderRef.current = false;
    }
  }, [bookingData]);

  return (
    !isLoading && (
      <DashboardLayout>
        <DashboardNavbar />
        <SubmitButton
          onClick={() => {
            setOpen(true);
            setFormIsEdit(false);
            setSelectedDate(null);
            setSelectedRange({ start: null, end: null });
            setEditData({});
          }}
          btnText={
            <>
              <Iconify icon="solar:add-square-bold" />
              Add Booking
            </>
          }
        />
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2, mb: 2 }}>
          {bookingColorOptions.map((option, index) => (
            <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  backgroundColor: option.color,
                }}
              />
              <span style={{ fontSize: "14px" }}>{option.label}</span>
            </Box>
          ))}
        </Box>
        <MDModal btnText="Add Booking" modalHead={`${formIsEdit ? "Edit" : "Add"} Booking`}>
          <BookingForm
            bookingListData={bookingListData}
            setOpenModal={setOpen}
            startDate={selectedRange.start}
            endDate={selectedRange.end}
            editData={editData}
            formEdit={formIsEdit}
            setFormEdit={setFormIsEdit}
            deleteId={deleteId}
            setDeleteId={setDeleteId}
          />
        </MDModal>
        <TableContainer>
          <Box sx={{ padding: "20px" }}>
            <MDBox
              ref={bookingRef}
              sx={{
                marginTop: "20px",
                maxWidth: "auto",
                height: "700px",
                margin: "0 auto",
              }}
            ></MDBox>
          </Box>
        </TableContainer>
      </DashboardLayout>
    )
  );
};

Booking.propTypes = {
  btnText: PropTypes.string,
  modalHead: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export default Booking;
